// src/App.jsx
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import Sidebar from "./components/Sidebar";
import { Spinner } from "./components/UI";

// Pages
import Login              from "./pages/Login";
import Register           from "./pages/Register";
import AdminDashboard     from "./pages/admin/Dashboard";
import Designers          from "./pages/admin/Designers";
import Contractors        from "./pages/admin/Contractors";
import Orders             from "./pages/admin/Orders";
import Commissions        from "./pages/admin/Commissions";
import Referrals          from "./pages/admin/Referrals";
import ProgramSettings    from "./pages/admin/ProgramSettings";
import Reports            from "./pages/admin/Reports";
import DesignerDashboard  from "./pages/designer/DesignerDashboard";
import DesignerOrders     from "./pages/designer/DesignerOrders";
import DesignerEarnings   from "./pages/designer/DesignerEarnings";
import DesignerReferral   from "./pages/designer/DesignerReferral";
import DesignerReferrals   from "./pages/designer/DesignerReferrals";
import DesignerProgram    from "./pages/designer/DesignerProgram";
import DesignerLogOrder   from "./pages/designer/DesignerLogOrder";
import DesignerInviteProgram from "./pages/designer/DesignerInviteProgram";

// ── Protected layout wrapper ──────────────────────────────────
function AppLayout({ children }) {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 960);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 960);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  return (
    <div style={{ display:"flex", minHeight:"100vh", position: "relative" }}>
      {isMobile && sidebarOpen && (
        <button
          type="button"
          aria-label="Close menu overlay"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            border: "none",
            background: "rgba(0, 0, 0, 0.35)",
            zIndex: 180,
          }}
        />
      )}
      <Sidebar isMobile={isMobile} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main style={{ flex:1, overflowY:"auto", background:"var(--surface)" }}>
        {isMobile && (
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 120,
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            background: "var(--surface)",
            borderBottom: "1px solid var(--border)",
          }}>
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              style={{
                border: "1px solid var(--border)",
                background: "var(--warm-white)",
                color: "var(--charcoal)",
                borderRadius: 4,
                width: 34,
                height: 30,
                fontSize: 18,
                lineHeight: 1,
              }}
              aria-label="Open menu"
            >
              ≡
            </button>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--mid)" }}>
              David Meyer Program
            </span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

// ── Route guards ──────────────────────────────────────────────
function RequireAuth({ children, requiredRole }) {
  const { user, role, loading } = useAuth();
  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (requiredRole && role !== requiredRole) return <Navigate to={role === "admin" ? "/admin" : "/portal"} replace />;
  return children;
}

/** Designer or contractor portal (same routes). */
function RequirePortal({ children }) {
  const { user, role, loading } = useAuth();
  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "admin") return <Navigate to="/admin" replace />;
  if (role !== "designer" && role !== "contractor") return <Navigate to="/login" replace />;
  return children;
}

function RootRedirect() {
  const { user, role, loading } = useAuth();
  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}><Spinner /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={role === "admin" ? "/admin" : "/portal"} replace />;
}

// ── App ───────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/login"    element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/"         element={<RootRedirect />} />

          {/* Admin routes */}
          <Route path="/admin" element={<RequireAuth requiredRole="admin"><AppLayout><AdminDashboard /></AppLayout></RequireAuth>} />
          <Route path="/admin/designers"   element={<RequireAuth requiredRole="admin"><AppLayout><Designers /></AppLayout></RequireAuth>} />
          <Route path="/admin/contractors" element={<RequireAuth requiredRole="admin"><AppLayout><Contractors /></AppLayout></RequireAuth>} />
          <Route path="/admin/orders"     element={<RequireAuth requiredRole="admin"><AppLayout><Orders /></AppLayout></RequireAuth>} />
          <Route path="/admin/commissions"element={<RequireAuth requiredRole="admin"><AppLayout><Commissions /></AppLayout></RequireAuth>} />
          <Route path="/admin/referrals"  element={<RequireAuth requiredRole="admin"><AppLayout><Referrals /></AppLayout></RequireAuth>} />
          <Route path="/admin/program"    element={<RequireAuth requiredRole="admin"><AppLayout><ProgramSettings /></AppLayout></RequireAuth>} />
          <Route path="/admin/reports"    element={<RequireAuth requiredRole="admin"><AppLayout><Reports /></AppLayout></RequireAuth>} />

          {/* Designer & contractor portal */}
          <Route path="/portal"           element={<RequirePortal><AppLayout><DesignerDashboard /></AppLayout></RequirePortal>} />
          <Route path="/portal/orders"    element={<RequirePortal><AppLayout><DesignerOrders /></AppLayout></RequirePortal>} />
          <Route path="/portal/log-order" element={<RequirePortal><AppLayout><DesignerLogOrder /></AppLayout></RequirePortal>} />
          <Route path="/portal/earnings"  element={<RequirePortal><AppLayout><DesignerEarnings /></AppLayout></RequirePortal>} />
          <Route path="/portal/referral"  element={<RequirePortal><AppLayout><DesignerReferral /></AppLayout></RequirePortal>} />
          <Route path="/portal/my-referrals" element={<RequirePortal><AppLayout><DesignerReferrals /></AppLayout></RequirePortal>} />
          <Route path="/portal/invite-designer" element={<RequirePortal><AppLayout><DesignerInviteProgram /></AppLayout></RequirePortal>} />
          <Route path="/portal/program"   element={<RequirePortal><AppLayout><DesignerProgram /></AppLayout></RequirePortal>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
